import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { RefreshCw, AlertTriangle, Home, Mail } from 'lucide-react';

import ETADisplay from '@/components/custom/ETADisplay';
import BusMap from '@/components/custom/BusMap';
import HorariosSalida from '@/components/custom/HorariosSalida';
import MetroAlerts from '@/components/custom/MetroAlerts';
import RecorridoRouteChips from '@/components/custom/RecorridoRouteChips';
import StatusBadge from '@/components/custom/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API_BASE as API } from '@/config/api';
import { normalizeRecorridoId, resolveRecorrido } from '@/data/recorridos/config';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useRecorridosVisibles } from '@/hooks/useRecorridosVisibles';
import { trackCambioSentido, trackVerParadero } from '@/lib/analytics';
import { mapAnchorParaderoForRecorrido, trayectoCatalogoInactivo } from '@/lib/paraderoRecorrido';
import { itinerarioPath, paraderoSearchString } from '@/lib/paraderoRoutes';
import { getStoredRecorridoId, setStoredRecorridoId } from '@/lib/recorridoStorage';

/** Refresco GET /trayectos/.../predicción (ms). Env: REACT_APP_PREDICCION_POLL_MS; default 3 s; mín. 2 s. */
const AUTO_REFRESH_INTERVAL = (() => {
  const raw = (process.env.REACT_APP_PREDICCION_POLL_MS || '').trim();
  const n = parseInt(raw, 10);
  const ms = Number.isNaN(n) ? 3000 : n;
  return Math.min(120000, Math.max(2000, ms));
})();

export default function ParaderoView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const recorridoParam = searchParams.get('recorrido');

  const { recorridosActivos, ready: recorridosVisiblesReady, trayectosFromApi } =
    useRecorridosVisibles();

  const activeRecorrido = useMemo(
    () => resolveRecorrido(recorridoParam, getStoredRecorridoId()),
    [recorridoParam]
  );

  const mapAnchor = useMemo(
    () => mapAnchorParaderoForRecorrido(activeRecorrido.id),
    [activeRecorrido.id]
  );

  const servicioReemplazoExterno = useMemo(() => {
    if (!recorridosVisiblesReady || !trayectosFromApi?.length) return false;
    const row = trayectosFromApi.find((t) => t.id === activeRecorrido.id);
    return trayectoCatalogoInactivo(row);
  }, [recorridosVisiblesReady, trayectosFromApi, activeRecorrido.id]);

  /** Patente asignada en backoffice al trayecto activo (prioridad sobre config estática). */
  const [busFilterFromOps, setBusFilterFromOps] = useState(null);

  const [sentido, setSentido] = useState('ida');

  useDocumentTitle(activeRecorrido.nombre);

  useEffect(() => {
    const id = normalizeRecorridoId(recorridoParam);
    if (id) setStoredRecorridoId(id);
  }, [recorridoParam]);

  /** Una consulta por recorrido abierto: alimenta la demanda por recorrido en Analytics. */
  useEffect(() => {
    trackVerParadero({
      recorridoId: activeRecorrido.id,
      paraderoId: searchParams.get('paraderoId'),
    });
    // Solo al cambiar de recorrido; el paradero de la URL se lee en ese momento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRecorrido.id]);

  /** Cambio de sentido hecho por la persona (el automático viene del GPS y no se registra). */
  const handleSentidoChange = useCallback(
    (next) => {
      setSentido(next);
      trackCambioSentido({ recorridoId: activeRecorrido.id, sentido: next });
    },
    [activeRecorrido.id]
  );

  useEffect(() => {
    setSentido('ida');
  }, [activeRecorrido.id]);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/asignaciones/buses`)
      .then((res) => {
        const list = res.data?.assignments || [];
        const match = list.find((a) => a.trayectoId === activeRecorrido.id);
        if (active) {
          setBusFilterFromOps(match?.patente?.trim() ? match.patente.trim() : null);
        }
      })
      .catch(() => {
        if (active) setBusFilterFromOps(null);
      });
    return () => {
      active = false;
    };
  }, [activeRecorrido.id]);

  const selectRecorrido = useCallback(
    (id) => {
      setStoredRecorridoId(id);
      const next = new URLSearchParams(paraderoSearchString({ recorridoId: id }));
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  const [prediccion, setPrediccion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  /** Solo el primer fetch tras cargar / cambiar recorrido debe usar pantalla de carga completa. */
  const expectInitialLoadRef = useRef(true);

  /**
   * El sentido del mapa (KML + paradas) debe seguir al GPS en cada refresco.
   * Antes solo se sincronizaba una vez y el mapa podía quedar en ida mientras la ETA ya indicaba vuelta (p. ej. T5A).
   */
  useEffect(() => {
    const s = prediccion?.sentidoBus;
    if (s !== 'ida' && s !== 'vuelta') return;
    if (!['t1a', 't1b', 't2', 't3', 't4', 't5a', 't5b'].includes(activeRecorrido.id)) return;
    setSentido(s);
  }, [prediccion?.sentidoBus, activeRecorrido.id]);

  useEffect(() => {
    expectInitialLoadRef.current = true;
  }, [activeRecorrido.id]);

  const fetchData = useCallback(
    async (isManualRefresh = false) => {
      try {
        if (isManualRefresh) {
          setRefreshing(true);
        } else if (expectInitialLoadRef.current) {
          setLoading(true);
        }
        setError(null);

        const predParams = {};
        if (busFilterFromOps) {
          predParams.patente = busFilterFromOps;
        }
        const prediccionRes = await axios.get(
          `${API}/trayectos/${activeRecorrido.id}/prediccion`,
          { params: predParams }
        );

        setPrediccion(prediccionRes.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching data:', err);
        }
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const detailStr =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => (typeof d === 'string' ? d : d?.msg || '')).filter(Boolean).join(' ')
              : null;
        const fallback404 =
          status === 404
            ? 'La API devolvió 404. Comprueba que el backend esté en marcha (p. ej. uvicorn en el puerto 8000), que el proxy /api en npm start apunte a ese puerto y, en Vercel, que exista MUNIBUS_BACKEND_URL o REACT_APP_BACKEND_URL en el build.'
            : 'Error al cargar la predicción del recorrido';
        setError(detailStr || fallback404);
      } finally {
        setLoading(false);
        setRefreshing(false);
        expectInitialLoadRef.current = false;
      }
    },
    [activeRecorrido.id, busFilterFromOps]
  );

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchData]);

  const handleManualRefresh = () => {
    fetchData(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-md space-y-6 px-4 pb-28 pt-24">
          <div className="space-y-2 pl-1">
            <div className="skeleton h-3 w-28 rounded-full" />
            <div className="skeleton h-10 w-4/5 rounded-xl" />
          </div>
          <div className="skeleton h-48 w-full rounded-2xl" />
          <div className="skeleton aspect-[4/3] w-full rounded-3xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md border-border/50 shadow-ambient">
          <CardHeader>
            <div className="mb-2 flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-8 w-8" />
              <CardTitle className="font-headline text-2xl">Error</CardTitle>
            </div>
            <CardDescription className="text-base">{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleManualRefresh} className="touch-target w-full" data-testid="retry-button">
              <RefreshCw className="mr-2 h-4 w-4" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!prediccion) {
    return null;
  }

  const ultPasada = (prediccion.ultimaParadaPasadaNombre || '').trim();
  const proxima = (prediccion.proximaParadaNombre || '').trim();

  /** Nombre del último paradero pasado (GPS), o próximo si aún no hay «pasada». */
  const tituloPrincipal = ultPasada || proxima || 'En recorrido';

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="paradero-view">
      <motion.header
        className="glass-header fixed left-0 right-0 top-0 z-50 border-b border-white/40"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="mx-auto flex h-[4.5rem] w-full max-w-md items-center justify-between px-5">
          <Link
            to="/"
            className="shrink-0 rounded-lg outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Ir al inicio"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/img/Logo-MuniBus.png`}
              alt=""
              className="h-10 w-auto"
            />
          </Link>
          <div className="flex shrink-0 items-center gap-2">
            <StatusBadge status={prediccion.estadoServicio} />
            <Button
              size="icon"
              variant="ghost"
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="touch-target shrink-0"
              data-testid="refresh-button"
              type="button"
              aria-label="Actualizar datos"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </motion.header>

      <main className="mx-auto max-w-md space-y-6 px-4 pb-4 pt-[5.5rem]">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28 }}
        >
          <RecorridoRouteChips
            activeId={activeRecorrido.id}
            onSelect={selectRecorrido}
            recorridos={recorridosActivos}
          />
          <div className="mt-2 flex justify-end px-1">
            <Link
              to={itinerarioPath({
                recorridoId: activeRecorrido.id,
              })}
              className="text-xs font-bold uppercase tracking-widest text-primary underline decoration-primary/40 underline-offset-4"
            >
              Ver Horarios e Itinerarios
            </Link>
          </div>
        </motion.div>

        <motion.section
          className="mb-2 pl-1"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="mb-1 text-[11px] font-extrabold uppercase tracking-widest text-muted-foreground">
            Último paradero
          </p>
          <h2
            className="font-headline text-3xl font-extrabold leading-tight tracking-tight text-foreground sm:text-4xl"
            data-testid="paradero-nombre"
          >
            {tituloPrincipal}
          </h2>
        </motion.section>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          <ETADisplay prediccion={prediccion} recorrido={activeRecorrido} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, delay: 0.08 }}
        >
          <HorariosSalida recorrido={activeRecorrido} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <BusMap
            mapAnchor={mapAnchor}
            prediccion={prediccion}
            recorrido={activeRecorrido}
            sentido={sentido}
            onSentidoChange={handleSentidoChange}
            busFilterId={busFilterFromOps}
            servicioReemplazoExterno={servicioReemplazoExterno}
            highlightStopName={ultPasada || null}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
        >
          <MetroAlerts />
        </motion.div>

        <div className="pb-2 pt-1 text-center">
          <a
            href="https://www.paine.cl/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-xl p-2 transition-opacity hover:opacity-85"
            aria-label="Visitar sitio Municipalidad de Paine"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/img/LogoPaine.svg`}
              alt="Municipalidad de Paine"
              className="h-10 w-auto"
            />
          </a>
        </div>
      </main>

      <nav
        className="glass-header fixed bottom-0 left-0 right-0 z-40 rounded-t-3xl border-t border-white/50 shadow-nav-up pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2"
        aria-label="Accesos rápidos"
      >
        <div className="mx-auto flex h-[4.5rem] max-w-md items-center justify-around px-8">
          <Link
            to="/"
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
              <Home className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Inicio</span>
          </Link>
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary disabled:opacity-50"
            aria-label="Actualizar"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <RefreshCw className={`h-6 w-6 ${refreshing ? 'animate-spin' : ''}`} />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Sincronizar</span>
          </button>
          <a
            href="mailto:contacto@paine.cl?subject=Reporte%20MuniBus"
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
            data-testid="report-link"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <Mail className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Reportar</span>
          </a>
        </div>
      </nav>
    </div>
  );
}
